import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import type { ToolCallEntry } from "@/lib/chat-stream";
import { cn } from "@/lib/utils";

const STATUS_LABEL: Record<ToolCallEntry["status"], string> = {
  requested: "queued",
  running: "running",
  succeeded: "ok",
  failed: "failed",
  denied: "denied",
  timeout: "timeout",
};

const STATUS_VARIANT: Record<
  ToolCallEntry["status"],
  "ok" | "warn" | "err" | "secondary" | "lemon"
> = {
  requested: "secondary",
  running: "lemon",
  succeeded: "ok",
  failed: "err",
  denied: "err",
  timeout: "warn",
};

function formatDuration(ms: number | undefined): string | undefined {
  if (ms == null || ms < 0) return undefined;
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  return s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function pretty(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function ToolCallBlock({ entry }: { entry: ToolCallEntry }) {
  // Auto-expand on terminal failure modes so users see the error
  // without having to click; everything else is collapsed by
  // default to keep the chat skim-friendly.
  const autoExpand =
    entry.status === "failed" || entry.status === "denied" || entry.status === "timeout";
  const [open, setOpen] = useState(autoExpand);

  const dur = formatDuration(entry.durationMs);
  const Chevron = open ? ChevronDown : ChevronRight;

  const hasArgs = entry.arguments && Object.keys(entry.arguments).length > 0;
  const hasOutput = entry.output != null && entry.output !== "";
  const hasError = !!entry.error;

  return (
    <div className="rounded-md border border-border bg-secondary/40">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-secondary/70"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="font-mono text-foreground">{entry.toolId}</span>
        <Badge variant={STATUS_VARIANT[entry.status]} className="ml-auto px-2 py-0 text-[10px]">
          {STATUS_LABEL[entry.status]}
        </Badge>
        {dur && <span className="font-mono text-[10px] text-muted-foreground">{dur}</span>}
      </button>
      {open && (hasArgs || hasOutput || hasError) && (
        <div className="grid gap-2 border-t border-border px-2.5 py-2 sm:grid-cols-2">
          {hasArgs && (
            <ToolCallSection label="Input">
              <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-card px-2 py-1.5 text-[11px] font-mono text-foreground">
                {pretty(entry.arguments)}
              </pre>
            </ToolCallSection>
          )}
          {(hasOutput || hasError) && (
            <ToolCallSection label={hasError ? "Error" : "Output"}>
              <pre
                className={cn(
                  "max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-card px-2 py-1.5 text-[11px] font-mono",
                  hasError ? "text-destructive" : "text-foreground",
                )}
              >
                {hasError ? entry.error : pretty(entry.output)}
              </pre>
            </ToolCallSection>
          )}
        </div>
      )}
    </div>
  );
}

function ToolCallSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

export function ToolCallList({ items }: { items: ToolCallEntry[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mb-2 flex flex-col gap-1.5">
      {items.map((entry) => (
        <ToolCallBlock key={entry.id} entry={entry} />
      ))}
    </div>
  );
}
